import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';

import { Box, Button, Card, Popover, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { StaticDateTimePicker } from '@mui/x-date-pickers';
import { DropZone } from 'components/common/DropZone';
import { TabPanel } from 'components/common/TabPanel';
import { EventChip, EventChipType } from 'components/event/EventChip';
import { EventSettingsDialog } from 'components/event/dialog/EventSettingsDialog';
import { MapTrackDialog } from 'components/event/dialog/MapTrackDialog';
import { ShareEventDialog } from 'components/event/dialog/ShareEventDialog';
import { RequestList } from 'components/request/RequestList';
import { TrackBuyActionMenu } from 'components/request/actionMenu/TrackBuyActionMenu';
import { TrackRequestActionMenu } from 'components/request/actionMenu/TrackRequestActionMenu';
import { RequestListDeletedToolbar } from 'components/request/requestList/RequestListDeletedToolbar';
import { RequestListToolbar } from 'components/request/requestList/RequestListToolbar';
import { useFirebase } from 'contexts/FirebaseContext';
import dayjs from 'dayjs';
import { useEvent } from 'hooks/useEvent';
import { useLiveEvent } from 'hooks/useLiveEvent';
import { useLoadingIndicator } from 'hooks/useLoadingIndicator';
import { useLocalDatabase } from 'hooks/useLocalDatabase';
import { useMusicLibraryImport } from 'hooks/useMusicLibraryImport';
import { usePlaylistExport } from 'hooks/usePlaylistExport';
import { useRequests } from 'hooks/useRequests';
import { useTrackMapping } from 'hooks/useTrackMapping';
import { useUser } from 'hooks/useUser';
import { LiveTrackResult } from 'models/LiveEvent';
import { LocalTrack } from 'models/LocalTrack';
import { TrackRequest } from 'models/TrackRequest';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { cleanArtistTitle } from 'utils/cleanArtistTitle';

export function EventDetails() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const { updateEvent, loadEvent, event } = useEvent(eventId ?? '');
  const [openMapDialog, setOpenMapDialog] = useState(false);
  const [openEditSettingsDialog, setOpenEditSettingsDialog] = useState(false);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [isLiveMode, setIsLiveMode] = useState(false);
  // const [event, setEvent] = useState<EventOccasion>();
  const { localDatabase, localDatabaseLoaded } = useLocalDatabase();
  const {} = usePlaylistExport(event!);
  const { user, setManageEvent } = useUser();
  const {
    fetchRequests,
    updateRequests,
    isLoadingRequests,
    requests,
    validRequests,
    processRequestMapping,
    deletedRequests,
    addRequestMapping,
    removeRequestMapping,
  } = useRequests(user?.uid);
  const { getMappedTrack } = useTrackMapping();
  const { listenToSearches, answerSearch } = useLiveEvent();
  const processedSearchesRef = useRef<Set<string>>(new Set());
  // const { getLocalPlaylist } = useLocalPlaylist();
  const [requestToMap, setRequestToMap] = useState<TrackRequest | null>(null);
  const [selectedRequests, setSelectedRequests] = useState<TrackRequest[]>([]);
  const playlistRequests = (selectedRequests.length ? selectedRequests : validRequests).filter(
    (r) => r.localTrack
  );
  const db = useFirebase().db;
  const [showImportStatus, setShowImportStatus] = useState(false);
  const maxDragAndDropFiles = 100;
  const { showLoadingIndicator, setIsLoading } = useLoadingIndicator(500);
  // const playlistTrackCount = validRequests.filter(({ localTrack }) => localTrack != null).length;
  // const hasSelection = selectedRequests.length > 0;

  // Popovers
  const eventDatePopoverId = useId();
  const [eventDatePopoverAnchorEl, setEventDatePopoverAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const eventExpireDatePopoverId = useId();
  const [eventExpireDatePopoverAnchorEl, setEventExpireDatePopoverAnchorEl] =
    React.useState<HTMLElement | null>(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>(
    'warning'
  );

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const { handleImport, importedCount, readCount, importing } = useMusicLibraryImport();

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setShowImportStatus(true); // Show the import status when files are dropped

      for (const file of acceptedFiles) {
        try {
          const path = (file as any).path;
          if (path) {
            handleImport(path);
          }
        } catch (error) {
          console.error('Error importing file:', error);
          setSnackbarMessage('Error importing file.'); // Set the Snackbar message
          setSnackbarSeverity('warning'); // Set the severity to warning
          setSnackbarOpen(true); // Open the Snackbar
        }
      }

      // Run automapping for all requests
      const processedRequests = await Promise.all(
        requests.map((request) => processRequestMapping(request, localDatabase, getMappedTrack))
      );

      // Store the processed requests back into the context
      if (eventId) {
        updateRequests(eventId, processedRequests).then(() => {
          handleRefresh();
        });
      }

      //setAllRequests([...processedRequests]);
    },
    [handleImport, requests, localDatabase, getMappedTrack]
  );

  // const handleChangeEvent = (values: Partial<EventOccasion>) => {
  //   const newEvent = { ...event, ...values } as EventOccasion;

  //   if (newEvent.date && (event?.expireDate || newEvent.date)) {
  //     const expireDate = newEvent.expireDate ?? event?.expireDate;
  //     if (newEvent.date && dayjs(newEvent.date).isBefore(dayjs(expireDate))) {
  //       newEvent.expireDate = newEvent.date;
  //     }
  //   }

  //   if (eventId && user) {
  //     setDoc(doc(db, `user/${user.uid}/events`, eventId), newEvent, { merge: true }).then(() => {
  //       setEvent(newEvent);
  //     });
  //   }
  // };

  // const loadEvent = async () => {
  //   if (eventId) {
  //     const eventRef = doc(db, `user/${user?.uid}/events`, eventId);
  //     const eventSnapshot = await getDoc(eventRef);

  //     if (eventSnapshot.exists()) {
  //       setEvent({ ...eventSnapshot.data(), ...{ id: eventId } } as EventOccasion);
  //       await fetchRequests(eventId);
  //     }
  //   }
  // };

  const handleCloseDialog = (ev?: React.MouseEvent<Element>) => {
    ev?.preventDefault();
    setOpenMapDialog(false);
    setOpenEditSettingsDialog(false);
    setOpenShareDialog(false);
  };

  const handleDeleteRequests = async (deleteRequests: TrackRequest[]) => {
    setSelectedRequests([]);

    deleteRequests = deleteRequests.map((request) => ({
      ...request,
      isDeleted: true,
      deleteDate: dayjs().toISOString(),
    }));

    if (eventId) {
      await updateRequests(eventId, deleteRequests);
    }
  };

  const handleRestoreRequests = async (restoreRequests: TrackRequest[]) => {
    setSelectedRequests([]);

    restoreRequests = restoreRequests.map((request) => ({
      ...request,
      isDeleted: false,
    }));

    if (eventId) {
      await updateRequests(eventId, restoreRequests);
    }
  };

  const handleAddRequestsDialog = () => {
    console.log('Add requests dialog not implemented yet');
  };

  const handleMapButtonClick = (request: TrackRequest) => {
    setRequestToMap(request);
    setOpenMapDialog(true);
  };

  const handleAddRequestMapping = async (localTrack: LocalTrack, rememberSelection: boolean) => {
    // setOpenMapDialog(false);

    if (eventId && requestToMap) {
      addRequestMapping(eventId, requestToMap, localTrack, rememberSelection);
    }
  };

  const handleRemoveMapping = async (localTrack: LocalTrack, rememberSelection: boolean) => {
    // setOpenMapDialog(false);

    if (eventId && requestToMap) {
      removeRequestMapping(eventId, requestToMap, rememberSelection);
    }
  };

  // const handleExportPlaylist = async () => {
  //   if (event && requests) {
  //     const playlist = await getLocalPlaylist(playlistRequests);

  //     if (playlist) {
  //       const content = createPlaylistCrate(playlist);

  //       if (!content) return;

  //       createDownloadFile(content, event.name, PlaylistExportType.CRATE);

  //       // const playlistCrateBuffer = window.electron?.createCrateBuffer?.(
  //       //   `${event.name}.crate`,
  //       //   playlist.map((track) => track.path)
  //       // );
  //       // if (playlistCrateBuffer) {
  //       //   await window.electron?.saveCrate(playlistCrateBuffer);
  //       // }

  //       // const playlistM3u = createPlaylistM3u(playlist);

  //       // if (playlistM3u) {
  //       //   createDownloadFile(playlistM3u, `${event.name}.m3u`);
  //       // }
  //     }
  //   }
  // };

  const handleRefresh = async () => {
    if (eventId) {
      await fetchRequests(eventId);
    }
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedRequests([]);
    setTabIndex(newValue);
  };

  const handleToggleDisableRequests = () => {
    updateEvent({ disableRequests: !event?.disableRequests });
  };

  useEffect(() => {
    if (!importing && showImportStatus) {
      // Set a timeout to hide the import status 20 seconds after importing is done
      const timer = setTimeout(() => {
        setShowImportStatus(false);
      }, 20000);

      return () => clearTimeout(timer); // Cleanup the timer on component unmount or re-run
    }
  }, [importing, showImportStatus]);

  useEffect(() => {
    handleRefresh();
  }, [event]);

  useEffect(() => {
    loadEvent();
  }, []);

  // useEffect(() => {
  //   if (!user || !eventId) return;

  //   const fetchEvent = async () => {
  //     await loadEvent();
  //   };

  //   fetchEvent();
  //   handleRefresh();
  // }, [db, user, eventId]);

  useEffect(() => {
    if (requests.length && localDatabaseLoaded) {
      handleRefresh();
    }
  }, [requests.length, localDatabaseLoaded]);

  useEffect(() => {
    setIsLoading(isLoadingRequests);
  }, [isLoadingRequests]);

  // Responder for live searches when event is live and local database is available
  useEffect(() => {
    if (!user?.uid || !eventId || !localDatabase || !event?.isLiveEvent) return;

    const unsubscribe = listenToSearches(user.uid, eventId, async (search) => {
      if (processedSearchesRef.current.has(search.id)) return;
      processedSearchesRef.current.add(search.id);

      try {
        console.log('[LiveSearch:EventDetails] Received search request:', search);
        const searchTitle = search.title?.trim() || undefined;
        const searchArtist = search.artist?.trim() || undefined;

        let results = await localDatabase.findTracks({
          title: searchTitle,
          artist: searchArtist,
          limit: 10,
        });

        if ((!results || results.length === 0) && searchTitle && searchArtist) {
          const cleaned = cleanArtistTitle({ title: searchTitle, artist: searchArtist });
          results = await localDatabase.findTracks({
            title: cleaned.title || undefined,
            artist: cleaned.artist || undefined,
            limit: 10,
          });
        }

        // Direct fallback to artist-only or title-only if still empty
        if ((!results || results.length === 0) && searchArtist) {
          results = await localDatabase.findTracksByArtist(searchArtist);
        }
        if ((!results || results.length === 0) && searchTitle) {
          results = await localDatabase.findTracksByTitle(searchTitle);
        }

        console.log('[LiveSearch:EventDetails] Found results:', results?.length || 0);

        const cleanResults: LiveTrackResult[] = (results || []).slice(0, 10).map((t) => ({
          id: t.id,
          title: t.title || '',
          artist: t.artist || '',
          album: t.album || '',
          genre: t.genre || '',
          bpm: t.bpm || undefined,
          key: t.key || undefined,
          path: t.path || undefined,
        }));

        await answerSearch(user.uid, eventId, search.id, cleanResults);
      } catch (err) {
        console.error('Error answering live search in EventDetails:', err);
      }
    });

    return () => unsubscribe();
  }, [user?.uid, eventId, localDatabase, event?.isLiveEvent, listenToSearches, answerSearch]);

  // const [open, setOpen] = useState(false);

  // console.log("EventDetails render: requests length =", requests.length, "validRequests length =", validRequests.length);

  // if (isLiveMode) {
  //   return (
  //     <EventLiveMode
  //       requests={validRequests}
  //       event={event}
  //       onExitLiveMode={() => setIsLiveMode(false)}
  //     />
  //   );
  // }

  return (
    <>
      <DropZone onDrop={handleDrop} maxFileCount={maxDragAndDropFiles} />
      {requestToMap && openMapDialog && (
        <MapTrackDialog
          open={openMapDialog}
          request={requestToMap}
          // mappedTrack={requestToMap.localTrack}
          onAddMapping={handleAddRequestMapping}
          onRemoveMapping={handleRemoveMapping}
          onClose={handleCloseDialog}
        />
      )}
      {openEditSettingsDialog && (
        <EventSettingsDialog
          open={openEditSettingsDialog}
          event={event}
          onCancel={handleCloseDialog}
          onSave={async (values) => {
            await updateEvent(values);
            handleCloseDialog();
          }}
        />
      )}
      {openShareDialog && event && (
        <ShareEventDialog eventId={event.id} open={openShareDialog} onClose={handleCloseDialog} />
      )}
      <Popover
        id={eventDatePopoverId}
        open={Boolean(eventDatePopoverAnchorEl)}
        anchorEl={eventDatePopoverAnchorEl}
        onClose={() => setEventDatePopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <StaticDateTimePicker
          minDate={dayjs()}
          defaultValue={event?.date ? dayjs(event?.date) : null}
          localeText={{
            toolbarTitle: t('eventDate', 'Event date'),
          }}
          onAccept={(value) => {
            updateEvent({ date: value?.toISOString() });
            setEventDatePopoverAnchorEl(null);
          }}
          slotProps={{
            actionBar: {
              actions: ['accept'],
            },
          }}
        />
      </Popover>
      <Popover
        id={eventExpireDatePopoverId}
        open={Boolean(eventExpireDatePopoverAnchorEl)}
        anchorEl={eventExpireDatePopoverAnchorEl}
        onClose={() => setEventExpireDatePopoverAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <StaticDateTimePicker
          minDate={dayjs()}
          maxDate={event?.date ? dayjs(event?.date) : undefined}
          defaultValue={event?.expireDate ? dayjs(event?.expireDate) : null}
          localeText={{
            toolbarTitle: t('acceptRequestsUntil', 'Accept requests until'),
          }}
          onAccept={(value) => {
            updateEvent({ expireDate: value?.toISOString() });
            setEventExpireDatePopoverAnchorEl(null);
          }}
          slotProps={{
            actionBar: {
              actions: ['accept'],
            },
          }}
        />
      </Popover>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h1" gutterBottom>
          {event?.name}
        </Typography>
        <Stack direction="row" spacing={1} marginBottom={2} useFlexGap flexWrap="wrap">
          <Tooltip title={t('eventDate', 'Event date')} placement="top">
            <span>
              <EventChip
                aria-describedby={eventDatePopoverId}
                onClick={(e) => setEventDatePopoverAnchorEl(e.currentTarget)}
                event={event}
                type={EventChipType.Date}
              />
            </span>
          </Tooltip>
          <Tooltip title={t('acceptRequestsUntil', 'Accept requests until')} placement="top">
            <span>
              <EventChip
                aria-describedby={eventExpireDatePopoverId}
                event={event}
                type={EventChipType.ExpireDate}
                onClick={(e) => setEventExpireDatePopoverAnchorEl(e.currentTarget)}
                onDelete={handleToggleDisableRequests}
              />
            </span>
          </Tooltip>
          {event?.isLiveEvent && <EventChip type={EventChipType.Live} event={event} />}
        </Stack>
        {event?.personalNotes && <Typography gutterBottom>{event?.personalNotes}</Typography>}
      </Box>
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {/* <ClientComponent>
          <Button
            startIcon={<QueueMusicIcon />}
            variant="contained"
            // color="primary"
            onClick={handleExportPlaylist}
            disabled={playlistTrackCount == 0}
          >
            Export ({playlistTrackCount})
          </Button>
        </ClientComponent> */}
        <Button
          startIcon={<ReplyIcon sx={{ transform: 'scaleX(-1)' }} />}
          variant="contained"
          color="secondary"
          onClick={() => setOpenShareDialog(true)}
        >
          {t('share', 'Share')}
        </Button>
        <Button
          startIcon={<EditIcon />}
          variant="contained"
          color="secondary"
          onClick={() => setOpenEditSettingsDialog(true)}
        >
          {t('editDetails', 'Edit Details')}
        </Button>

        {/* <ClientComponent>
          <Button
            startIcon={<PlayArrowIcon />}
            variant="contained"
            color="primary"
            onClick={() => {
              if (eventId) {
                window.electron?.openLiveModeWindow(eventId);
              }
            }}
          >
            {t('liveMode', 'Live Mode')}
          </Button>
        </ClientComponent> */}

        {/* <Button
          startIcon={<PlayArrowIcon />}
          variant="contained"
          color="primary"
          onClick={() => setIsLiveMode(true)}
        >
          {t('liveMode', 'Live Mode')}
        </Button> */}
      </Stack>
      <Box>
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Card
            elevation={0}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              borderEndStartRadius: 0,
              borderEndEndRadius: 0,
            }}
          >
            <Tabs value={tabIndex} onChange={handleChangeTab}>
              <Tab label={t('requests', 'Requests')} />
              {/* <Tab label={t('playlist', "Playlist")} /> */}
              <Tab label={t('trash', 'Trash')} />
            </Tabs>
          </Card>
          <TabPanel index={0} value={tabIndex}>
            <RequestList
              requests={validRequests}
              selectedRequests={selectedRequests}
              onSelectRequest={(selectedRows) => {
                setSelectedRequests(selectedRows);
              }}
              onMapButtonClick={(request) => {
                handleMapButtonClick(request);
              }}
              requestColumns={['date', 'state']}
              actions={(params) => (
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  <Box>
                    <TrackBuyActionMenu request={params.row} />
                  </Box>
                  <Box>
                    <TrackRequestActionMenu
                      request={params.row}
                      onMapButtonClick={() => handleMapButtonClick(params.row)}
                      onDeleteRequest={() => handleDeleteRequests([params.row])}
                      onRestoreRequest={() => handleRestoreRequests([params.row])}
                    />
                  </Box>
                </Stack>
              )}
              showToolbar
              slots={{
                toolbar: (props) => (
                  <RequestListToolbar
                    event={event!}
                    requests={validRequests}
                    selectedRequests={selectedRequests}
                    playlistRequests={playlistRequests}
                    loading={showLoadingIndicator}
                    onDelete={handleDeleteRequests}
                    // onExport={handleExportPlaylist}
                    onReload={handleRefresh}
                    onAddRequestsDialog={handleAddRequestsDialog}
                    {...props}
                  />
                ),
              }}
            />
          </TabPanel>
          {/* <TabPanel index={1} value={tabIndex}>
            <RequestList
              requests={playlistRequests}
              selectedRequests={selectedRequests}
              onSelectRequest={(selectedRows) => {
                setSelectedRequests(selectedRows);
              }}
              onMapButtonClick={(request) => {
                handleMapButtonClick(request);
              }}
              requestColumns={['deleteDate', 'state']}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    deleteDate: true,
                  },
                },
                sorting: {
                  sortModel: [
                    {
                      field: 'deleteDate',
                      sort: 'asc',
                    },
                  ],
                },
              }}
              showToolbar
            />
          </TabPanel> */}
          <TabPanel index={1} value={tabIndex}>
            <RequestList
              requests={deletedRequests}
              selectedRequests={selectedRequests}
              onSelectRequest={(selectedRows) => {
                setSelectedRequests(selectedRows);
              }}
              onMapButtonClick={(request) => {
                handleMapButtonClick(request);
              }}
              requestColumns={['deleteDate', 'state']}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    deleteDate: true,
                  },
                },
                sorting: {
                  sortModel: [
                    {
                      field: 'deleteDate',
                      sort: 'asc',
                    },
                  ],
                },
              }}
              actions={(params) => (
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  <Box>
                    <TrackBuyActionMenu request={params.row} />
                  </Box>
                  <Box>
                    <TrackRequestActionMenu
                      request={params.row}
                      onMapButtonClick={() => handleMapButtonClick(params.row)}
                      onDeleteRequest={() => handleDeleteRequests([params.row])}
                      onRestoreRequest={() => handleRestoreRequests([params.row])}
                    />
                  </Box>
                </Stack>
              )}
              showToolbar
              slots={{
                toolbar: (props) => (
                  <RequestListDeletedToolbar
                    event={event!}
                    requests={deletedRequests}
                    selectedRequests={selectedRequests}
                    onRestore={handleRestoreRequests}
                    loading={showLoadingIndicator}
                    {...props}
                  />
                ),
              }}
            />
          </TabPanel>
        </Box>
      </Box>
    </>
  );
}
